<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Placements" class="px-4 pr-5 mb-5" />
      <PlacementsFilters
        class="px-5 mb-sm-5"
        @search="handleSearchClick"
        @clear="handleClearClick"
      ></PlacementsFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          :headers="headers"
          :items="filteredPlacementList"
          class="elevation-2"
        >
          <template v-slot:item.placementType="{ item }">
            <span :class="{
              'text-in-progress' : item.dealStatusId === dealStatuses.InProgress,
              'text-new': item.dealStatusId === dealStatuses.New,
              'text-rejected': item.dealStatusId === dealStatuses.Rejected,
              'text-approved': item.dealStatusId === dealStatuses.Approved,
              '' : item.dealStatusId === null || item.dealStatusId === undefined
            }">{{ item.placementType }}</span>
          </template>
          <template v-slot:item.approvalDate="{ item }">
            {{ item.approvalDate | formatDate }}
          </template>
          <template v-slot:item.startDate="{ item }">
            {{ item.duration }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              v-if="isViewOnly(item)"
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editItem($event, item)"
            >
              <v-icon color="primary">visibility</v-icon>
            </v-btn>
            <v-btn
              v-if="!isViewOnly(item)"
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editItem($event, item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              v-if="isAdmin"
              class="mx-1 my-1"
              color="red"
              outlined
              @click="deleteItemHandler(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="deleteConfirmationText"
      :toolbarColor="'primary'"
      @cancel-click="cancelDelete"
      @ok-click="confirmDelete"
      :showModal="showDeleteConfirmationDialog"
    ></ConfirmationModal>
    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ConfirmationModal } from '@/components/dialogs'
import PlacementsFilters from './PlacementsFilters'
import { dealStatuses } from '@/lib/enum/dealStatuses'
import { placementTypes } from '@/lib/enum/placementTypes'
import { assignmentEditRoute, directPlacementEditRoute, dealWizardRoute } from '@/lib/constants/routes'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { userIsAdmin, getCurrentUser, userHasRoles } from '@/lib/authentication'

export default {
  components: {
    BottomSpacer,
    SimplePageHeader,
    PlacementsFilters,
    ConfirmationModal
  },
  data () {
    return {
      dealStatuses,
      placementTypes,
      filteredPlacementList: [],
      showDeleteConfirmationDialog: false,
      itemToDelete: null
    }
  },
  computed: {
    ...mapFields('placements', [
      'placementList'
    ]),
    ...mapFields('placementFilters', [
      'placementTypeId'
    ]),
    headers () {
      const cols = [
        { text: 'Client', value: 'clientName' },
        { text: 'Consultant', value: 'consultantName' },
        { text: 'Job Title', value: 'jobTitle' },
        { text: 'Type', value: 'placementType' }
      ]
      if (this.placementTypeId === placementTypes.Deal) {
        cols.push({ text: 'Approval Date', value: 'approvalDate', width: '150px' })
      }
      cols.push({ text: 'Start-End', value: 'startDate' })
      cols.push({ text: 'Actions', width: '200px', value: 'action', align: 'center', sortable: false })
      return cols
    },
    isAdmin () {
      return this.userIsAdmin()
    },
    isReadOnlyAssignment () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
    },
    isReadOnlyDirectPlacement () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, SALES_PERSON, MANAGER] })
    },
    isReadOnlyDeal () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    deleteConfirmationText () {
      if (!this.itemToDelete) return ''
      if (this.itemToDelete.placementTypeId === placementTypes.Assignment) return 'Are you sure you want to delete the selected contract?'
      if (this.itemToDelete.placementTypeId === placementTypes.DirectPlacement) return 'Are you sure you want to delete the selected permanent placement?'
      if (this.itemToDelete.placementTypeId === placementTypes.Deal) return 'Are you sure you want to delete the selected deal?'
      return 'Are you sure you want to delete the selected record?'
    }
  },
  methods: {
    ...mapActions('placements', ['getPlacements']),
    ...mapActions('placementFilters', ['clearFilters']),
    ...mapActions('assignment', ['deleteAssignment']),
    ...mapActions('directPlacement', ['deleteDirectPlacement']),
    ...mapActions('dealV2', ['deleteDeal']),
    getCurrentUser,
    userHasRoles,
    userIsAdmin,
    isViewOnly (item) {
      if (item.placementTypeId === placementTypes.Assignment) return this.isReadOnlyAssignment
      if (item.placementTypeId === placementTypes.DirectPlacement) return this.isReadOnlyDirectPlacement
      if (item.placementTypeId === placementTypes.Deal) {
        return item.dealStatusId === dealStatuses.Approved ||
          (item.dealStatusId === dealStatuses.New && !this.isAdmin) ||
          this.isReadOnlyDeal
      }
      return false
    },
    getPlacementFilters () {
      return this.$store.getters['placementFilters/getSearchObject']
    },
    normalizeValue (value) {
      return value === null || value === undefined ? '' : String(value).trim().toLowerCase()
    },
    // Filters the already-loaded placementList in memory by Job Title.
    // These three fields are intentionally kept as client-side filters to avoid a server round-trip
    // on every keystroke / partial match.
    applyClientSideFilters () {
      const filters = this.getPlacementFilters()
      this.filteredPlacementList = this.placementList.filter((placement) => {
        const matchesJobTitle = !filters.jobTitle || this.normalizeValue(placement.jobTitle).includes(this.normalizeValue(filters.jobTitle))
        return matchesJobTitle
      })
    },
    // Called when the user clicks Search.
    // - If a server-side filter (placementTypeId, or any future server-side filters) has a value,
    //   we first fetch a narrowed result set from the server, then apply the client-side filters
    //   on top of that result.
    // - If no server-side filter is active, we skip the network call and only run the
    //   client-side filters against the list that is already in memory.
    handleSearchClick () {
      const filters = this.getPlacementFilters()
      if (filters.placementTypeId ||
          filters.onlyFinalizedContracts ||
          filters.dealStatusId ||
          filters.businessEntityId ||
          filters.clientId ||
          filters.consultantId ||
          filters.consultantName
      ) {
        this.getPlacements().then(() => {
          this.applyClientSideFilters()
        })
      } else {
        this.applyClientSideFilters()
      }
    },
    // Called when the user clicks Clear.
    // Always reloads the full list from the server so that any previously applied server-side
    // filter (e.g. placementTypeId) is discarded and the table is fully restored,
    // then re-applies client-side filters (which will all be empty after a clear).
    handleClearClick () {
      this.getPlacements().then(() => {
        this.applyClientSideFilters()
      })
    },
    editItem (event, item) {
      let routeName = null
      if (item.placementTypeId === placementTypes.Assignment) {
        routeName = assignmentEditRoute.name
      } else if (item.placementTypeId === placementTypes.DirectPlacement) {
        routeName = directPlacementEditRoute.name
      } else if (item.placementTypeId === placementTypes.Deal) {
        routeName = dealWizardRoute.name
      }
      if (!routeName) return
      if (event.ctrlKey) {
        const route = this.$router.resolve({ name: routeName, params: { id: item.entityId } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: routeName, params: { id: item.entityId } })
      }
    },
    deleteItemHandler (item) {
      this.itemToDelete = item
      this.showDeleteConfirmationDialog = true
    },
    cancelDelete () {
      this.showDeleteConfirmationDialog = false
      this.itemToDelete = null
    },
    confirmDelete () {
      const item = this.itemToDelete
      this.showDeleteConfirmationDialog = false
      this.itemToDelete = null
      let deletePromise = null
      if (item.placementTypeId === placementTypes.Assignment) {
        deletePromise = this.deleteAssignment(item.entityId)
      } else if (item.placementTypeId === placementTypes.DirectPlacement) {
        deletePromise = this.deleteDirectPlacement(item.entityId)
      } else if (item.placementTypeId === placementTypes.Deal) {
        deletePromise = this.deleteDeal(item.entityId)
      }
      if (deletePromise) {
        deletePromise.then(() => {
          this.getPlacements().then(() => {
            this.applyClientSideFilters()
          })
        })
      }
    }
  },
  mounted () {
    this.clearFilters()
    this.getPlacements().then((placements) => {
      this.filteredPlacementList = placements
    })
  }
}
</script>
